import { fetchAuthSession } from '@aws-amplify/auth';
import type {
  Client,
  Project,
  TaskItem,
  TaskParentType,
  Contractor,
  CreateClientRequest,
  UpdateClientRequest,
  CreateProjectRequest,
  UpdateProjectRequest,
  CreateTaskItemRequest,
  UpdateTaskItemRequest,
  DashboardTaskItem,
  CreateContractorRequest,
  UpdateContractorRequest,
  Product,
  CreateProductRequest,
  UpdateProductRequest,
  ProjectProduct,
  CreateProjectProductRequest,
  UpdateProjectProductRequest,
  Estimate,
  CreateEstimateRequest,
  UpdateEstimateRequest,
  ProjectEstimateView,
  EstimateWithScopes,
  EstimateOption,
  CreateEstimateOptionRequest,
  UpdateEstimateOptionRequest,
  EstimateUploadUrlResponse,
  EstimateDownloadUrlResponse,
  ContractorCoiUploadUrlResponse,
  ContractorCoiDownloadUrlResponse,
  ContractorCoiExtractResponse,
  ContractorLicenseUploadUrlResponse,
  ContractorLicenseDownloadUrlResponse,
  Invoice,
  CreateInvoiceRequest,
  UpdateInvoiceRequest,
  Payment,
  CreatePaymentRequest,
  UpdatePaymentRequest,
  BudgetSummary,
  TrackedDevice,
  UpdateDeviceLabelRequest,
  SearchIndexResponse,
  ShoppingListItem,
} from '@bpd/shared';

const PROJECTS_API_URL = import.meta.env.VITE_PROJECTS_API_URL;

async function authedFetch<T>(path: string, init?: RequestInit): Promise<T> {
  if (!PROJECTS_API_URL) throw new Error('Projects API is not configured');

  const session = await fetchAuthSession({ forceRefresh: false });
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) throw new Error('Not authenticated');

  const res = await fetch(`${PROJECTS_API_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${idToken}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `Request failed with status ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const projectsApi = {
  isConfigured: (): boolean => Boolean(PROJECTS_API_URL),

  // ─── Clients ──────────────────────────────────────────────────────────
  listClients: () => authedFetch<Client[]>('/clients'),
  createClient: (body: CreateClientRequest) =>
    authedFetch<Client>('/clients', { method: 'POST', body: JSON.stringify(body) }),
  updateClient: (id: string, body: UpdateClientRequest) =>
    authedFetch<Client>(`/clients/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteClient: (id: string) => authedFetch<void>(`/clients/${id}`, { method: 'DELETE' }),

  // ─── Projects ─────────────────────────────────────────────────────────
  listProjects: (clientId: string) => authedFetch<Project[]>(`/clients/${clientId}/projects`),
  createProject: (body: CreateProjectRequest) =>
    authedFetch<Project>('/projects', { method: 'POST', body: JSON.stringify(body) }),
  updateProject: (id: string, clientId: string, body: UpdateProjectRequest) =>
    authedFetch<Project>(`/projects/${id}?clientId=${encodeURIComponent(clientId)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteProject: (id: string, clientId: string) =>
    authedFetch<void>(`/projects/${id}?clientId=${encodeURIComponent(clientId)}`, { method: 'DELETE' }),

  // ─── TaskItems (tasks and, one level deeper, subtasks) ───────────────
  listItems: (parentType: TaskParentType, parentId: string) =>
    authedFetch<TaskItem[]>(`/items?parentType=${parentType}&parentId=${encodeURIComponent(parentId)}`),
  createItem: (body: CreateTaskItemRequest) =>
    authedFetch<TaskItem>('/items', { method: 'POST', body: JSON.stringify(body) }),
  updateItem: (id: string, parentType: TaskParentType, parentId: string, body: UpdateTaskItemRequest) =>
    authedFetch<TaskItem>(
      `/items/${id}?parentType=${parentType}&parentId=${encodeURIComponent(parentId)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  deleteItem: (id: string, parentType: TaskParentType, parentId: string) =>
    authedFetch<void>(`/items/${id}?parentType=${parentType}&parentId=${encodeURIComponent(parentId)}`, {
      method: 'DELETE',
    }),
  reorderItems: (parentType: TaskParentType, parentId: string, items: { id: string; sequence: number }[]) =>
    authedFetch<{ success: boolean }>(
      `/items/reorder?parentType=${parentType}&parentId=${encodeURIComponent(parentId)}`,
      { method: 'POST', body: JSON.stringify({ items }) },
    ),
  /** Reparents a subtask — to standalone (toParentType: 'project', toParentId: the project id) or
   * under a different task (toParentType: 'task'). fromParentId is always a task id (only subtasks
   * are reparentable). */
  moveItem: (
    id: string,
    fromParentId: string,
    toParentType: TaskParentType,
    toParentId: string,
  ) =>
    authedFetch<TaskItem>(
      `/items/${id}/move?parentType=task&parentId=${encodeURIComponent(fromParentId)}`,
      { method: 'POST', body: JSON.stringify({ parentType: toParentType, parentId: toParentId }) },
    ),

  // ─── Dashboard ────────────────────────────────────────────────────────
  listDashboardItems: () => authedFetch<DashboardTaskItem[]>('/dashboard'),
  /** Persists a Dashboard drag — each item may belong to a different project/task, since
   * dashboardOrder is comparable across all of them (unlike reorderItems' per-project sequence). */
  reorderDashboard: (
    items: { id: string; parentType: TaskParentType; parentId: string; dashboardOrder: number }[],
  ) => authedFetch<{ success: boolean }>('/dashboard/reorder', { method: 'POST', body: JSON.stringify({ items }) }),

  // ─── Search / shopping list ───────────────────────────────────────────
  getSearchIndex: () => authedFetch<SearchIndexResponse>('/search-index'),
  getShoppingList: () => authedFetch<ShoppingListItem[]>('/shopping-list'),

  // ─── Contractors ──────────────────────────────────────────────────────
  listContractors: () => authedFetch<Contractor[]>('/contractors'),
  createContractor: (body: CreateContractorRequest) =>
    authedFetch<Contractor>('/contractors', { method: 'POST', body: JSON.stringify(body) }),
  updateContractor: (id: string, body: UpdateContractorRequest) =>
    authedFetch<Contractor>(`/contractors/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteContractor: (id: string) => authedFetch<void>(`/contractors/${id}`, { method: 'DELETE' }),

  // ─── Contractor Certificate of Insurance attachment (one PDF per contractor) ──────────
  /** Requests a presigned S3 PUT URL, then the caller uploads the file directly to S3 (not
   * through this API), calls extractContractorCoi to auto-read it, and finally calls
   * updateContractor to persist the (possibly admin-corrected) fields plus the key. */
  getContractorCoiUploadUrl: (id: string, contentType: string, fileName: string) =>
    authedFetch<ContractorCoiUploadUrlResponse>(`/contractors/${id}/coi-upload-url`, {
      method: 'POST',
      body: JSON.stringify({ contentType, fileName }),
    }),
  /** Server-side read of the just-uploaded CoI PDF - returns extracted fields for review, does
   * NOT itself save anything onto the Contractor. */
  extractContractorCoi: (id: string, key: string) =>
    authedFetch<ContractorCoiExtractResponse>(`/contractors/${id}/coi-extract`, {
      method: 'POST',
      body: JSON.stringify({ key }),
    }),
  getContractorCoiDownloadUrl: (id: string) =>
    authedFetch<ContractorCoiDownloadUrlResponse>(`/contractors/${id}/coi-download-url`),
  deleteContractorCoiAttachment: (id: string) =>
    authedFetch<void>(`/contractors/${id}/coi-attachment`, { method: 'DELETE' }),

  // ─── Contractor license attachments (multiple licenses per contractor) ────────────────
  /** The license's own fields (number/type/issuer/expiration/notes) are saved via the ordinary
   * updateContractor whole-array replace, same as `people` — these three only cover the optional
   * scanned copy per license. */
  getContractorLicenseUploadUrl: (contractorId: string, licenseId: string, contentType: string, fileName: string) =>
    authedFetch<ContractorLicenseUploadUrlResponse>(
      `/contractors/${contractorId}/licenses/${licenseId}/upload-url`,
      { method: 'POST', body: JSON.stringify({ contentType, fileName }) },
    ),
  getContractorLicenseDownloadUrl: (contractorId: string, licenseId: string) =>
    authedFetch<ContractorLicenseDownloadUrlResponse>(
      `/contractors/${contractorId}/licenses/${licenseId}/download-url`,
    ),
  deleteContractorLicenseAttachment: (contractorId: string, licenseId: string) =>
    authedFetch<void>(`/contractors/${contractorId}/licenses/${licenseId}/attachment`, { method: 'DELETE' }),

  // ─── Products (catalog) ──────────────────────────────────────────────
  listProducts: () => authedFetch<Product[]>('/products'),
  createProduct: (body: CreateProductRequest) =>
    authedFetch<Product>('/products', { method: 'POST', body: JSON.stringify(body) }),
  updateProduct: (id: string, body: UpdateProductRequest) =>
    authedFetch<Product>(`/products/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteProduct: (id: string) => authedFetch<void>(`/products/${id}`, { method: 'DELETE' }),

  // ─── Project products (purchase line items) ──────────────────────────
  listProjectProducts: (projectId: string) =>
    authedFetch<ProjectProduct[]>(`/project-products?projectId=${encodeURIComponent(projectId)}`),
  createProjectProduct: (body: CreateProjectProductRequest) =>
    authedFetch<ProjectProduct>('/project-products', { method: 'POST', body: JSON.stringify(body) }),
  updateProjectProduct: (id: string, projectId: string, body: UpdateProjectProductRequest) =>
    authedFetch<ProjectProduct>(`/project-products/${id}?projectId=${encodeURIComponent(projectId)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteProjectProduct: (id: string, projectId: string) =>
    authedFetch<void>(`/project-products/${id}?projectId=${encodeURIComponent(projectId)}`, {
      method: 'DELETE',
    }),

  // ─── Estimates / Proposals ────────────────────────────────────────────
  /** This project's view of every estimate attached to it — still shaped with `taskItemId` (see
   * ProjectEstimateView), so ProjectEstimatesSection barely had to change even though an Estimate
   * itself is no longer owned by one project. */
  listEstimates: (projectId: string) =>
    authedFetch<ProjectEstimateView[]>(`/estimates?projectId=${encodeURIComponent(projectId)}`),
  /** Every estimate from one contractor, with every project/task it's attached to — powers
   * ContractorsPage's caret-expand estimate list. */
  listEstimatesByContractor: (contractorId: string) =>
    authedFetch<EstimateWithScopes[]>(`/estimates?contractorId=${encodeURIComponent(contractorId)}`),
  createEstimate: (body: CreateEstimateRequest) =>
    authedFetch<Estimate>('/estimates', { method: 'POST', body: JSON.stringify(body) }),
  updateEstimate: (id: string, body: UpdateEstimateRequest) =>
    authedFetch<Estimate>(`/estimates/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteEstimate: (id: string) => authedFetch<void>(`/estimates/${id}`, { method: 'DELETE' }),

  // ─── Estimate quote attachment (one file per estimate, shared across every project it's
  // attached to) ───────────────────────────────────────────────────────
  /** Requests a presigned S3 PUT URL, then the caller uploads the file directly to S3 (not
   * through this API) and finally calls updateEstimate to persist the returned key. */
  getEstimateUploadUrl: (id: string, contentType: string, fileName: string) =>
    authedFetch<EstimateUploadUrlResponse>(`/estimates/${id}/upload-url`, {
      method: 'POST',
      body: JSON.stringify({ contentType, fileName }),
    }),
  getEstimateDownloadUrl: (id: string) =>
    authedFetch<EstimateDownloadUrlResponse>(`/estimates/${id}/download-url`),
  deleteEstimateAttachment: (id: string) =>
    authedFetch<void>(`/estimates/${id}/attachment`, { method: 'DELETE' }),

  // ─── Estimate options (scope lines) ───────────────────────────────────
  listEstimateOptions: (estimateId: string) =>
    authedFetch<EstimateOption[]>(`/estimate-options?estimateId=${encodeURIComponent(estimateId)}`),
  createEstimateOption: (body: CreateEstimateOptionRequest) =>
    authedFetch<EstimateOption>('/estimate-options', { method: 'POST', body: JSON.stringify(body) }),
  updateEstimateOption: (id: string, estimateId: string, body: UpdateEstimateOptionRequest) =>
    authedFetch<EstimateOption>(`/estimate-options/${id}?estimateId=${encodeURIComponent(estimateId)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteEstimateOption: (id: string, estimateId: string) =>
    authedFetch<void>(`/estimate-options/${id}?estimateId=${encodeURIComponent(estimateId)}`, {
      method: 'DELETE',
    }),

  // ─── Invoices (actual bills) ────────────────────────────────────────────
  listInvoices: (projectId: string) =>
    authedFetch<Invoice[]>(`/invoices?projectId=${encodeURIComponent(projectId)}`),
  createInvoice: (body: CreateInvoiceRequest) =>
    authedFetch<Invoice>('/invoices', { method: 'POST', body: JSON.stringify(body) }),
  updateInvoice: (id: string, projectId: string, body: UpdateInvoiceRequest) =>
    authedFetch<Invoice>(`/invoices/${id}?projectId=${encodeURIComponent(projectId)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  // Vendor-invoice file (one per invoice) — same presigned-PUT flow as the estimate quote file.
  getInvoiceUploadUrl: (id: string, projectId: string, contentType: string, fileName: string) =>
    authedFetch<EstimateUploadUrlResponse>(`/invoices/${id}/upload-url?projectId=${encodeURIComponent(projectId)}`, {
      method: 'POST',
      body: JSON.stringify({ contentType, fileName }),
    }),
  getInvoiceDownloadUrl: (id: string, projectId: string) =>
    authedFetch<EstimateDownloadUrlResponse>(`/invoices/${id}/download-url?projectId=${encodeURIComponent(projectId)}`),
  deleteInvoiceAttachment: (id: string, projectId: string) =>
    authedFetch<void>(`/invoices/${id}/attachment?projectId=${encodeURIComponent(projectId)}`, { method: 'DELETE' }),
  deleteInvoice: (id: string, projectId: string) =>
    authedFetch<void>(`/invoices/${id}?projectId=${encodeURIComponent(projectId)}`, { method: 'DELETE' }),

  // ─── Payments (against an Invoice) ──────────────────────────────────────
  listPayments: (invoiceId: string) =>
    authedFetch<Payment[]>(`/payments?invoiceId=${encodeURIComponent(invoiceId)}`),
  createPayment: (body: CreatePaymentRequest) =>
    authedFetch<Payment>('/payments', { method: 'POST', body: JSON.stringify(body) }),
  updatePayment: (id: string, invoiceId: string, body: UpdatePaymentRequest) =>
    authedFetch<Payment>(`/payments/${id}?invoiceId=${encodeURIComponent(invoiceId)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deletePayment: (id: string, invoiceId: string) =>
    authedFetch<void>(`/payments/${id}?invoiceId=${encodeURIComponent(invoiceId)}`, { method: 'DELETE' }),

  // ─── Budget rollup ─────────────────────────────────────────────────────
  getBudget: () => authedFetch<BudgetSummary>('/budget'),

  // ─── Devices (cross-admin visibility + friendly labels) ────────────────
  listAllDevices: () => authedFetch<TrackedDevice[]>('/devices'),
  /** Backend confirms only the written label row, not a full TrackedDevice — callers should merge
   * the returned `label` into their existing device record rather than replace it wholesale. */
  updateDeviceLabel: (deviceKey: string, body: UpdateDeviceLabelRequest) =>
    authedFetch<{ label: string }>(`/devices/${encodeURIComponent(deviceKey)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  adminForgetDevice: (deviceKey: string, ownerUsername: string) =>
    authedFetch<void>(`/devices/${encodeURIComponent(deviceKey)}?ownerUsername=${encodeURIComponent(ownerUsername)}`, {
      method: 'DELETE',
    }),
};
